import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";
import { ArrowLeft, Plane, Sparkles, Crown, Syringe, Package, AlertCircle, Info, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TravelMode() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [selectedPetId, setSelectedPetId] = useState("");
  const [destination, setDestination] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const isPremium = user?.plan === "premium";

  useEffect(() => {
    fetch(`${API}/pets`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setPets(data); if (data.length > 0) setSelectedPetId(data[0].pet_id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locals inside async closures are not deps
  }, []);

  const selectedPet = pets.find(p => p.pet_id === selectedPetId);

  const handleGenerate = async () => {
    if (!destination.trim()) { setError(t("travel.needDestination")); return; }
    if (!selectedPetId) { setError(t("travel.needPet")); return; }
    setLoading(true); setResult(null); setError("");
    try {
      const vaccinations = selectedPet?.vaccinations?.map(v => v.name) || [];
      const res = await fetch(`${API}/travel/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          destination,
          pet_type: selectedPet?.species || "dog",
          pet_vaccinations: vaccinations,
          breed: selectedPet?.breed || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || t("travel.genericError")); return; }
      setResult(data);
    } catch { setError(t("common.networkError")); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }}>
      <Navigation />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4 mb-8 animate-fade-in-up">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-xl hover:bg-white hover:shadow-sm text-slate-500 transition-all">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-extrabold text-slate-800 font-['Nunito']">{t("travel.title")}</h1>
              <span className="text-xs bg-[#E3F2FD] text-[#1565C0] px-2.5 py-1 rounded-full font-bold flex items-center gap-1">
                <Sparkles size={11} /> {t("travel.badge")}
              </span>
            </div>
            <p className="text-slate-500 text-sm">{t("travel.subtitle")}</p>
          </div>
        </div>

        {/* Premium gate */}
        {!isPremium ? (
          <div className="bg-white rounded-3xl border-2 border-[#FFD54F] shadow-md p-8 text-center animate-fade-in-up">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#FFF9C4] flex items-center justify-center mb-5">
              <Crown size={28} className="text-[#FF8A65]" />
            </div>
            <h2 className="text-2xl font-extrabold text-slate-800 font-['Nunito'] mb-3">{t("travel.premiumGateTitle")}</h2>
            <p className="text-slate-600 mb-2 max-w-md mx-auto">{t("travel.premiumGateBody")}</p>
            <p className="text-slate-500 text-sm mb-8">{t("travel.premiumGatePrice")}</p>
            <button onClick={() => navigate("/settings")} className="px-8 py-3.5 rounded-full bg-[#FF8A65] text-white font-semibold hover:bg-[#FF7043] transition-all shadow-md hover:-translate-y-0.5">
              {t("common.upgradeToPremium")}
            </button>
          </div>
        ) : (
          <>
            {/* Input card */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 mb-6 animate-fade-in-up">
              <div className="relative mb-4">
                <img
                  src="https://images.pexels.com/photos/6716737/pexels-photo-6716737.jpeg"
                  alt="Dog in car"
                  className="w-full h-36 object-cover rounded-2xl opacity-90"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-slate-900/50 to-transparent flex items-center px-6">
                  <div>
                    <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">{t("travel.whereGoing")}</p>
                    <p className="text-white text-xl font-bold font-['Nunito']">{t("travel.letAiPlan")}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {pets.length > 1 && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("travel.selectPet")}</label>
                    <select value={selectedPetId} onChange={e => setSelectedPetId(e.target.value)} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40">
                      {pets.map(p => <option key={p.pet_id} value={p.pet_id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("travel.destination")}</label>
                  <div className="relative">
                    <Plane size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="destination-input"
                      value={destination}
                      onChange={e => setDestination(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleGenerate()}
                      placeholder={t("travel.destinationPlaceholder")}
                      className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40"
                    />
                  </div>
                </div>

                {selectedPet && (
                  <div className="bg-[#FFF5E6] rounded-xl p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#FF8A65]/20 flex items-center justify-center flex-shrink-0">
                      <Info size={14} className="text-[#FF8A65]" />
                    </div>
                    <p className="text-xs text-slate-600">
                      {t("travel.generatingFor")} <strong>{selectedPet.name}</strong> ({selectedPet.species}{selectedPet.breed ? `, ${selectedPet.breed}` : ""})
                      {selectedPet.vaccinations?.length > 0 ? ` · ${t("travel.vaccinationsOnRecord", { count: selectedPet.vaccinations.length })}` : ""}
                    </p>
                  </div>
                )}

                {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-xl">{error}</p>}

                <button
                  data-testid="generate-travel-btn"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full py-3.5 rounded-full bg-[#FF8A65] text-white font-semibold hover:bg-[#FF7043] transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-md"
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      <span className="ai-loading">{t("travel.aiThinking")}</span>
                    </>
                  ) : (
                    <><Sparkles size={16} /> {t("travel.generateBtn")}</>
                  )}
                </button>
              </div>
            </div>

            {/* Results */}
            {result && !result.error && (
              <div className="space-y-4 animate-fade-in-up">
                {result.required_vaccines?.length > 0 && (
                  <ResultSection icon={Syringe} title={t("travel.requiredVaccines")} color="mint" items={result.required_vaccines} />
                )}
                {result.health_certificate && (
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full bg-[#E3F2FD] flex items-center justify-center">
                        <AlertCircle size={16} className="text-[#42A5F5]" />
                      </div>
                      <h3 className="font-bold text-slate-800 font-['Nunito']">{t("travel.healthCertificate")}</h3>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{result.health_certificate}</p>
                  </div>
                )}
                {result.airline_requirements?.length > 0 && (
                  <ResultSection icon={Plane} title={t("travel.airlineRequirements")} color="blue" items={result.airline_requirements} />
                )}
                {result.packing_checklist?.length > 0 && (
                  <ResultSection icon={Package} title={t("travel.packingChecklist")} color="yellow" items={result.packing_checklist} />
                )}
                {result.quarantine_info && result.quarantine_info !== "None" && (
                  <div className="bg-[#FFEBEE] rounded-3xl border border-red-200 p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertCircle size={16} className="text-red-500" />
                      </div>
                      <h3 className="font-bold text-slate-800 font-['Nunito']">{t("travel.quarantineRequirements")}</h3>
                    </div>
                    <p className="text-sm text-red-800 leading-relaxed">{result.quarantine_info}</p>
                  </div>
                )}
                {result.important_notes?.length > 0 && (
                  <ResultSection icon={Info} title={t("travel.importantNotes")} color="orange" items={result.important_notes} />
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function ResultSection({ icon: Icon, title, color, items }) {
  const colors = {
    mint: { bg: "bg-[#E8F5E9]", icon: "text-[#388E3C]", dot: "#81C784" },
    blue: { bg: "bg-[#E3F2FD]", icon: "text-[#1565C0]", dot: "#64B5F6" },
    yellow: { bg: "bg-[#FFFDE7]", icon: "text-[#F57F17]", dot: "#FFD54F" },
    orange: { bg: "bg-[#FFF3E0]", icon: "text-[#E65100]", dot: "#FF8A65" },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-9 h-9 rounded-full ${c.bg} flex items-center justify-center`}>
          <Icon size={16} className={c.icon} strokeWidth={2.5} />
        </div>
        <h3 className="font-bold text-slate-800 font-['Nunito']">{title}</h3>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={`${title}-${i}-${typeof item === "string" ? item.slice(0, 24) : ""}`} className="flex items-start gap-3 text-sm text-slate-700">
            <CheckCircle2 size={15} className={c.icon} style={{ color: c.dot, marginTop: "2px", flexShrink: 0 }} />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
